// Per-branch actuals. The ONLY place that talks to the `branch_actuals` table.
// Feeds data-driven branch shares (a branch's historical fraction of its hub).
// Mirrors customerActualsApi.

import { supabase } from "../../lib/supabase";
import { fetchAllPaged } from "../../lib/dbPaginate";

export interface BranchActual {
  branchId: string;
  hubId: string;
  date: string; // YYYY-MM-DD
  actualVolume: number;
}

interface BranchActualRow {
  id: string;
  account_id: string;
  branch_id: string;
  hub_id: string;
  date: string;
  actual_volume: number;
}

/** DB row -> app BranchActual. */
export function rowToBranchActual(row: BranchActualRow): BranchActual {
  return {
    branchId: row.branch_id,
    hubId: row.hub_id,
    date: row.date,
    actualVolume: Number(row.actual_volume),
  };
}

/** App BranchActual -> DB row fields. */
export function branchActualInputToRow(accountId: string, a: BranchActual) {
  return {
    account_id: accountId,
    branch_id: a.branchId,
    hub_id: a.hubId,
    date: a.date,
    actual_volume: a.actualVolume,
  };
}

/** Load all branch actuals in the account within a date range (inclusive). */
export async function listAccountBranchActuals(
  accountId: string,
  startDate: string,
  endDate: string
): Promise<BranchActual[]> {
  // Paged: 50 branches × 6 months can exceed PostgREST's 1,000-row cap.
  const rows = await fetchAllPaged<BranchActualRow>((from, to) =>
    supabase
      .from("branch_actuals")
      .select("*")
      .eq("account_id", accountId)
      .gte("date", startDate)
      .lte("date", endDate)
      .order("id")
      .range(from, to)
  );
  return rows.map(rowToBranchActual);
}

/** Delete every branch actual for the account (irreversible). */
export async function deleteAllBranchActuals(accountId: string): Promise<void> {
  const { error } = await supabase.from("branch_actuals").delete().eq("account_id", accountId);
  if (error) throw error;
}

/** Upsert branch actuals (one row per branch/date). */
export async function saveBranchActuals(accountId: string, entries: BranchActual[]): Promise<void> {
  if (entries.length === 0) return;
  const rows = entries.map((e) => branchActualInputToRow(accountId, e));
  const { error } = await supabase.from("branch_actuals").upsert(rows, { onConflict: "branch_id,date" });
  if (error) throw error;
}
